/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import logger from '@/logger';
import CategoryPageApi from '@/api/categoryPage';
import CourseListingApi from '@/api/courseListing';
import CourseDescription from '@/api/courseDescription';
import CategoryPageContext from '@/context/CategoryPageContext';
import { ICategoryPage } from '@/interfaces/categoryPage';
import { CategoryPageFilterPayload, ICourseList } from '@/interfaces/courseListing';
import { ICourses } from '@/interfaces/homePage';
import useSlugTitleMapFunction from '@/store/useSlugTitleMap';
import getCategoryCanonicalUrl from '@/utils/categoryCanonical.utils';
import { domainUrl } from '@/utils/common/env.constants';
import { getNavigationContext } from '@/utils/navigationContext';
import CategoryDescriptionPage from '../../components/CategoryPage';
import { dataLayer } from '../../utils/common/dataLayer.utils';
import useUserDetails from '../../store/userDetails';

const CategoryPage = ({
  slug,
  response,
  canonicalUrl,
}: {
  slug: string;
  response: any;
  canonicalUrl: string;
}) => {
  const [categoryDetails, setCategoryDetails] = useState<ICategoryPage | null>(null);
  const { slugTitleMap, setSlugTitleMap } = useSlugTitleMapFunction();
  // const { user, isLoading: isLoadingProfile } = useContext(UserDetailsContext);
  const { userDetails: user, profileLoader: isLoadingProfile } = useUserDetails();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();
  const navigationContext = getNavigationContext(router.asPath);
  const DataLayerParam = () => {
    const DataLayerCategoryPage = {
      user_id: user?._id,
      category_name: categoryDetails?.title || categoryDetails?.categoryDetails?.name,
      category_id: categoryDetails?._id || categoryDetails?.categoryDetails?._id,
      category_exam: categoryDetails?.categoryDetails?.exam,
      category_sub_exam: categoryDetails?.categoryDetails?.subExam,
      page_ref: router.query.page_ref || navigationContext.pageRef || navigationContext.from,
      source: router.query.position || navigationContext.position || '',
    };
    dataLayer({ ...DataLayerCategoryPage, page_name: 'category_page', event: 'category_page' });
  };
  useEffect(() => {
    if (categoryDetails && !isLoadingProfile && router.isReady) {
      DataLayerParam();
    }
  }, [
    categoryDetails,
    router.query.position,
    router.query.page_ref,
    isLoadingProfile,
    router.isReady,
  ]);

  const hanleSlugTitleMap = (slug: string, title: string) => {
    const tempObj = { ...slugTitleMap, [slug]: title };
    setSlugTitleMap(tempObj);
  };

  useEffect(() => {
    if (response) {
      // Handle the standard category page response structure
      hanleSlugTitleMap(slug, response.data?.title);
      setCategoryDetails(response.data);
    }
  }, [slug, response]);

  const useStore = useMemo(() => {
    return {
      categoryDetails,
      setCategoryDetails,
      loading: isLoading,
      setIsLoading,
    };
  }, [categoryDetails, setCategoryDetails, isLoading]);

  return (
    <>
      <Head>
        <meta name="description" content={response?.data?.seo?.description} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        <meta
          property="og:image"
          content={
            // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
            response?.data?.seo?.ogImage && response?.data?.seo?.ogImage.trim() !== ''
              ? response?.data?.seo?.ogImage
              : response?.data?.categoryImage?.url
          }
        />
        <meta
          property="og:title"
          content={
            response?.data?.seo?.ogTitle ? response?.data?.seo?.ogTitle : response?.data?.seo?.title
          }
        />
        <meta
          property="og:description"
          content={
            response?.data?.seo?.ogDescription
              ? response?.data?.seo?.ogDescription
              : response?.data?.seo?.description
          }
        />
        <meta property="og:image:width" content="456" />
        <meta property="og:image:height" content="256" />

        <meta property="twitter:url" content={canonicalUrl} />
        <meta
          property="twitter:title"
          content={
            response?.data?.seo?.twitterTitle
              ? response?.data?.seo?.twitterTitle
              : response?.data?.seo?.title
          }
        />
        <meta
          property="twitter:description"
          content={
            response?.data?.seo?.twitterDescription
              ? response?.data?.seo?.twitterDescription
              : response?.data?.seo?.description
          }
        />
        <meta
          property="twitter:image"
          content={
            // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
            response?.data?.seo?.twitterImage && response?.data?.seo?.twitterImage.trim() !== ''
              ? response?.data?.seo?.twitterImage
              : response?.data?.categoryImage?.url
          }
        />
        <meta property="twitter:image:width" content="456" />
        <meta property="twitter:image:height" content="256" />
      </Head>

      <CategoryPageContext.Provider value={useStore}>
        <CategoryDescriptionPage categoryId={response?.data?.categoryId || ''} />
      </CategoryPageContext.Provider>
    </>
  );
};

// export const getStaticPaths = async () => {
//   return { paths: [], fallback: 'blocking' };
// };

// console.log('params', params);
// console.log('queryString', resolvedUrl);
// if (params.categorySlug === 'watch') {
//   return {
//     redirect: {
//       destination: `http://pw-video-player-staging.s3-website.ap-south-1.amazonaws.com${resolvedUrl}`,
//       permanent: true,
//     },
//   };
// }

// export async function getStaticProps({ params }: { params: { categorySlug: string } }) {
//   try {
//     const response = await CategoryPageApi.getCategoryData(params.categorySlug);
//     logger.info(`GET /category/${params.categorySlug} PAGE: category`);
//     return {
//       props: {
//         slug: params.categorySlug,
//         response,
//       },
//       revalidate: 10,
//     };
//   } catch (error: any) {
//     logger.error(
//       `GET /category/${params.categorySlug} ${error.error ? error.error : error} PAGE: category`
//     );
//     return { notFound: true, revalidate: 1 };
//   }
// }

export async function getServerSideProps({ params, query, resolvedUrl }: any) {
  try {
    logger.info(`GET /category/${params.categorySlug} PAGE: category`);
    if (params.categorySlug === 'watch') {
      return {
        redirect: {
          destination: `https://pw-video-player-staging.s3-website.ap-south-1.amazonaws.com${resolvedUrl}`,
          permanent: true,
        },
      };
    }

    if (!query.token) {
      const redirectUrl = await CourseDescription.getServerSideRedirectUrl(
        `${domainUrl}/${params.categorySlug}`
      );
      if (redirectUrl) {
        return {
          redirect: { destination: redirectUrl, permanent: true },
        };
      }
    }

    const response = await CategoryPageApi.getCategoryData(params.categorySlug);
    const categoryId = (response as { data?: { categoryId?: string } })?.data?.categoryId;
    let courses: ICourses[] = [];

    if (categoryId) {
      try {
        const coursesResponse = (await CourseListingApi.getFilteredCourse(
          {
            filterConfigSrc: 'category_page',
            filterConfigCohortId: categoryId,
          } as CategoryPageFilterPayload,
          1,
          50
        )) as ICourseList;
        courses = coursesResponse?.data?.courses ?? [];
      } catch (error: any) {
        logger.error(
          `GET /category/${params.categorySlug} courses ${error.error || error} PAGE: category`
        );
      }
    }

    return {
      props: {
        slug: params.categorySlug,
        response,
        courses,
        canonicalUrl: getCategoryCanonicalUrl((response as any)?.data, params.categorySlug),
      },
    };
  } catch (error: any) {
    logger.error(`GET /category/${params.categorySlug} ${error.error || error} PAGE: category`);
    return {
      notFound: true,
    };
  }
}

export default CategoryPage;
